/**
 * voiceChain — the publisher-side vocal chain.
 *
 * Raw mic track → Web Audio graph → processed track that mediasoup publishes:
 *
 *   Source(raw mic) → inputGain → highpass → gate(worklet) → compressor
 *     → makeupGain → limiter → analyser → MediaStreamAudioDestination
 *
 * Key property: the processed track (the destination node's track) NEVER
 * changes identity. A mic device swap replaces the source node inside the
 * graph, so mediaService's replaceTracks/producer plumbing sees the same
 * track throughout and needs no changes.
 *
 * Raw-track ownership: mediaService stops only the tracks in the stream it
 * was handed — which contains the processed track, not the raw mic. This
 * module OWNS the raw track: replaceSource/detachSource/dispose stop it,
 * keeping the OS mic-in-use indicator honest.
 *
 * Every node is always connected; "off" states park params at neutral values
 * (unity ratio, 10 Hz highpass, -100 dB gate threshold) so toggling a stage
 * never reconnects the graph. With everything off the chain is transparent.
 */

import { getSharedAudioContext, resumeAudioContext } from '../utils/audioContext';
import mediaService from './mediaService';

// User-facing settings and their defaults. EC/NS live here for storage
// convenience but are NOT chain params — they alter getUserMedia constraints
// (App re-acquires the mic when they change).
export const DEFAULT_MIC_PROCESSING = {
  inputGainDb: 0, // -24..+24
  highPass: false, // 200 Hz low-cut
  compressor: 'off', // off | light | medium | broadcast
  gate: 'off', // off | light | medium | strong
  gateFloorDb: -15, // -40..-6, how far a closed gate attenuates
  disableEchoCancellation: false,
  disableNoiseSuppression: false,
};

export const COMPRESSOR_LEVELS = ['off', 'light', 'medium', 'broadcast'];
export const GATE_LEVELS = ['off', 'light', 'medium', 'strong'];

// "Gate strength": stronger = higher threshold = gates more.
const GATE_THRESHOLDS_DB = { off: -100, light: -58, medium: -48, strong: -38 };

const COMPRESSOR_PRESETS = {
  off: { threshold: 0, knee: 0, ratio: 1, attack: 0.003, release: 0.25, makeupDb: 0 },
  light: { threshold: -24, knee: 12, ratio: 2, attack: 0.02, release: 0.25, makeupDb: 2 },
  medium: { threshold: -28, knee: 10, ratio: 4, attack: 0.01, release: 0.2, makeupDb: 4 },
  broadcast: { threshold: -32, knee: 6, ratio: 8, attack: 0.003, release: 0.15, makeupDb: 6 },
};

// Hard safety limiter near -1 dBFS (a DynamicsCompressor at 20:1 with a 1 ms
// attack overshoots by well under a dB). Tenant-disableable, not user-facing.
const LIMITER_ON = { threshold: -1, knee: 0, ratio: 20, attack: 0.001, release: 0.1 };
const LIMITER_OFF = { threshold: 0, knee: 0, ratio: 1, attack: 0.003, release: 0.25 };

const RAMP = 0.03; // s — setTargetAtTime time constant for audible params

const dbToLin = (db) => Math.pow(10, db / 20);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// The worklet module loads once per page (it registers on the shared context,
// which every VoiceChain instance uses). Memoized promise; resolves false on
// any failure so chains build gateless instead of breaking.
let gateModuleP = null;
function loadGateModule(ctx) {
  if (!gateModuleP) {
    const url = `${process.env.PUBLIC_URL || ''}/worklets/noise-gate.worklet.js`;
    gateModuleP =
      ctx.audioWorklet && ctx.audioWorklet.addModule
        ? ctx.audioWorklet
            .addModule(url)
            .then(() => true)
            .catch(() => false)
        : Promise.resolve(false);
  }
  return gateModuleP;
}

export class VoiceChain {
  constructor() {
    this.ctx = null;
    this.settings = { ...DEFAULT_MIC_PROCESSING };
    this.limiterEnabled = true; // tenant flag (Instance.settings.voiceLimiterEnabled)
    this.gateAvailable = false; // true once the worklet node is live in the graph
    this._nodes = null;
    this._rawTrack = null;
    this._onRawEnded = null;
    this._levelBuf = null;
    this._gateState = { open: true, gainDb: 0 };
    this._gateListeners = new Set();
  }

  // ---- graph -------------------------------------------------------------

  _ensureGraph() {
    if (this._nodes) {
      const t = this._nodes.destination.stream.getAudioTracks()[0];
      if (t && t.readyState === 'live') return this._nodes;
      // The processed track was stopped externally — mediaService stops every
      // track in the stream it was handed (unpublish, or a swap to a mic-less
      // device), and that includes ours. A stopped track can't be revived, so
      // rebuild the graph for a fresh destination track. Gate-LED subscribers
      // survive the rebuild.
      const listeners = new Set(this._gateListeners);
      this.dispose();
      this._gateListeners = listeners;
    }
    const ctx = getSharedAudioContext();
    if (!ctx) return null; // no Web Audio: callers fall back to the raw track
    this.ctx = ctx;

    const inputGain = ctx.createGain();
    const highpass = ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 10; // "off" = inaudible corner
    highpass.Q.value = 0.707;
    const compressor = ctx.createDynamicsCompressor();
    const makeupGain = ctx.createGain();
    const limiter = ctx.createDynamicsCompressor();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    const destination = ctx.createMediaStreamDestination();

    // Gate is spliced in asynchronously once the worklet module loads; until
    // then highpass feeds the compressor directly.
    inputGain.connect(highpass);
    highpass.connect(compressor);
    compressor.connect(makeupGain);
    makeupGain.connect(limiter);
    limiter.connect(analyser);
    analyser.connect(destination);

    this._nodes = { source: null, inputGain, highpass, gate: null, compressor, makeupGain, limiter, analyser, destination };
    this._levelBuf = new Float32Array(analyser.fftSize);
    this._applyAll();

    loadGateModule(ctx).then((ok) => {
      // The graph may have been disposed (or a gate already inserted by an
      // earlier resolve) while the module was loading.
      if (!ok || !this._nodes || this._nodes.gate) return;
      try {
        const gate = new AudioWorkletNode(ctx, 'noise-gate', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
        });
        gate.port.onmessage = (e) => {
          const d = e && e.data;
          if (!d || typeof d.open !== 'boolean') return;
          this._gateState = { open: d.open, gainDb: d.gainDb || 0 };
          this._gateListeners.forEach((cb) => {
            try {
              cb(this._gateState);
            } catch (err) {
              /* listener errors must not kill the port */
            }
          });
        };
        // Splice: highpass → gate → compressor. The gate defaults to
        // pass-through (-100 dB threshold) so the one-time reconnect is
        // inaudible.
        this._nodes.highpass.disconnect(this._nodes.compressor);
        this._nodes.highpass.connect(gate);
        gate.connect(this._nodes.compressor);
        this._nodes.gate = gate;
        this.gateAvailable = true;
        this._applyGate();
      } catch (e) {
        /* node construction failed — stay gateless, control stays disabled */
      }
    });

    return this._nodes;
  }

  _setParam(param, value) {
    if (!param) return;
    const t = this.ctx ? this.ctx.currentTime : 0;
    try {
      param.cancelScheduledValues(t);
      param.setTargetAtTime(value, t, RAMP);
    } catch (e) {
      try {
        param.value = value;
      } catch (e2) {
        /* noop */
      }
    }
  }

  _applyAll() {
    if (!this._nodes) return;
    this._applyInputGain();
    this._applyHighPass();
    this._applyCompressor();
    this._applyLimiter();
    this._applyGate();
  }

  _applyInputGain() {
    const db = clamp(Number(this.settings.inputGainDb) || 0, -24, 24);
    this._setParam(this._nodes.inputGain.gain, dbToLin(db));
  }

  _applyHighPass() {
    this._setParam(this._nodes.highpass.frequency, this.settings.highPass ? 200 : 10);
  }

  _applyCompressor() {
    const p = COMPRESSOR_PRESETS[this.settings.compressor] || COMPRESSOR_PRESETS.off;
    const c = this._nodes.compressor;
    this._setParam(c.threshold, p.threshold);
    this._setParam(c.knee, p.knee);
    this._setParam(c.ratio, p.ratio);
    this._setParam(c.attack, p.attack);
    this._setParam(c.release, p.release);
    this._setParam(this._nodes.makeupGain.gain, dbToLin(p.makeupDb));
  }

  _applyLimiter() {
    const p = this.limiterEnabled ? LIMITER_ON : LIMITER_OFF;
    const l = this._nodes.limiter;
    this._setParam(l.threshold, p.threshold);
    this._setParam(l.knee, p.knee);
    this._setParam(l.ratio, p.ratio);
    this._setParam(l.attack, p.attack);
    this._setParam(l.release, p.release);
  }

  _applyGate() {
    const gate = this._nodes && this._nodes.gate;
    if (!gate) return;
    const th = GATE_THRESHOLDS_DB[this.settings.gate];
    const floor = clamp(Number(this.settings.gateFloorDb) || -15, -40, -6);
    // Detector params, not in the audio path — snap instantly (no ramp) so
    // "Off" bypasses immediately.
    try {
      const t = this.ctx.currentTime;
      gate.parameters.get('thresholdDb').setValueAtTime(th == null ? -100 : th, t);
      gate.parameters.get('floorDb').setValueAtTime(floor, t);
    } catch (e) {
      /* noop */
    }
  }

  // ---- source / track lifecycle -------------------------------------------

  /**
   * Route `rawTrack` (a live mic MediaStreamTrack) through the chain and
   * return the processed track to publish. Builds the graph on first call.
   * If Web Audio is unavailable, returns the raw track (feature off).
   */
  attachSource(rawTrack) {
    const nodes = this._ensureGraph();
    if (!nodes) return rawTrack;
    resumeAudioContext();
    if (rawTrack === this._rawTrack) return this.getProcessedTrack();

    // Connect the new source BEFORE stopping the old one so a device swap has
    // no dead-air gap.
    let source;
    try {
      source = this.ctx.createMediaStreamSource(new MediaStream([rawTrack]));
      source.connect(nodes.inputGain);
    } catch (e) {
      // Couldn't wrap the track (dead track / bizarre browser) — leave the
      // previous source in place and let the caller publish raw.
      return rawTrack;
    }

    this._teardownSource();
    nodes.source = source;
    this._rawTrack = rawTrack;
    // Hardware death (USB mic unplugged, permission revoked) ends the RAW
    // track only — the processed track lives on. Mirror today's semantics
    // (watchers go silent) and release the device.
    this._onRawEnded = () => this.detachSource();
    rawTrack.addEventListener('ended', this._onRawEnded);
    return this.getProcessedTrack();
  }

  /** Device hot-swap: new raw mic track, or null when the new device has none. */
  replaceSource(rawTrack) {
    if (!rawTrack) {
      this.detachSource();
      return null;
    }
    return this.attachSource(rawTrack);
  }

  /** Stop + disconnect the raw mic; keep the graph warm for the next attach. */
  detachSource() {
    this._teardownSource();
    if (this._nodes) this._nodes.source = null;
  }

  _teardownSource() {
    const nodes = this._nodes;
    if (nodes && nodes.source) {
      try {
        nodes.source.disconnect();
      } catch (e) {
        /* noop */
      }
    }
    if (this._rawTrack) {
      if (this._onRawEnded) {
        this._rawTrack.removeEventListener('ended', this._onRawEnded);
        this._onRawEnded = null;
      }
      try {
        this._rawTrack.stop();
      } catch (e) {
        /* noop */
      }
      this._rawTrack = null;
    }
  }

  /** Full teardown (logout/unmount). The shared AudioContext stays open. */
  dispose() {
    this._teardownSource();
    const nodes = this._nodes;
    if (nodes) {
      Object.values(nodes).forEach((n) => {
        if (n && n.disconnect) {
          try {
            n.disconnect();
          } catch (e) {
            /* noop */
          }
        }
      });
      if (nodes.gate) {
        try {
          nodes.gate.port.onmessage = null;
        } catch (e) {
          /* noop */
        }
      }
    }
    this._nodes = null;
    this._levelBuf = null;
    this.gateAvailable = false;
    this._gateListeners.clear();
  }

  // ---- settings / readouts -------------------------------------------------

  /** Merge + live-apply a partial settings object (no re-publish needed). */
  updateSettings(partial) {
    if (!partial) return;
    this.settings = { ...this.settings, ...partial };
    if (!this._nodes) return;
    if ('inputGainDb' in partial) this._applyInputGain();
    if ('highPass' in partial) this._applyHighPass();
    if ('compressor' in partial) this._applyCompressor();
    if ('gate' in partial || 'gateFloorDb' in partial) this._applyGate();
  }

  /** Tenant flag (Instance.settings.voiceLimiterEnabled). */
  setLimiterEnabled(enabled) {
    this.limiterEnabled = enabled !== false;
    if (this._nodes) this._applyLimiter();
  }

  getProcessedTrack() {
    const nodes = this._nodes;
    if (!nodes) return null;
    return nodes.destination.stream.getAudioTracks()[0] || null;
  }

  /** Post-chain level for meters: {rms, peak} in 0..1. */
  getLevel() {
    const nodes = this._nodes;
    if (!nodes || !this._levelBuf) return { rms: 0, peak: 0 };
    try {
      nodes.analyser.getFloatTimeDomainData(this._levelBuf);
    } catch (e) {
      return { rms: 0, peak: 0 };
    }
    const buf = this._levelBuf;
    let sum = 0;
    let peak = 0;
    for (let i = 0; i < buf.length; i++) {
      const s = buf[i];
      sum += s * s;
      const a = s < 0 ? -s : s;
      if (a > peak) peak = a;
    }
    return { rms: Math.sqrt(sum / buf.length), peak };
  }

  getGateState() {
    return this._gateState;
  }

  /** Subscribe to gate open/close activity (the UI LED). Returns unsubscribe. */
  onGateState(cb) {
    this._gateListeners.add(cb);
    return () => this._gateListeners.delete(cb);
  }
}

// The publish singleton: App routes the live mic through this instance.
const voiceChain = new VoiceChain();
export default voiceChain;

// Throwaway instances for the settings-panel preview meter (tune the chain
// before going live without touching the publish graph).
export const createPreviewChain = () => new VoiceChain();

/**
 * Self-listen monitor: hear your own mic (through the vocal chain) the way
 * others would. Two hard requirements this helper centralizes:
 *
 *  1. Its capture runs with echoCancellation OFF. Monitoring your own voice
 *     through a page-audio element makes the browser's AEC treat that
 *     playback as far-end echo and cyclically cancel it from the mic input —
 *     audible as the volume "pumping" up and down. An EC-free capture is the
 *     standard mic-test approach (the room never hears this capture anyway).
 *  2. While the monitor lives, the OUTBOUND mic (audio producer) is paused —
 *     the test must never broadcast. dispose() restores the exact prior
 *     state (a user who was already muted stays muted).
 *
 * Returns { chain, stream, dispose } — stream is the POST-chain audio to
 * feed the monitor element; dispose tears everything down.
 */
export async function createSelfListenMonitor({
  audioDeviceId = '',
  settings = null,
  limiterEnabled = true,
  // Live mode: while publishing, don't open a PARALLEL capture (a concurrent
  // EC'd capture of the same device contaminates the raw one — Chrome's
  // per-device pipeline lets the echo canceller converge on the monitor
  // playback and subtract your sustained voice to zero). Instead, swap the
  // LIVE chain's source to a raw capture for the monitor's lifetime (the
  // outbound producer is paused the whole time), then restore the user's
  // real constraints on dispose.
  liveChain = null,
  restoreConstraints = null, // { echoCancellation, noiseSuppression } for the live-mode restore
} = {}) {
  // Mute-to-room FIRST so not even the acquisition window can leak the test.
  const producer = mediaService.audioProducer;
  const wasPaused = producer ? !!producer.paused : true;
  if (producer && !wasPaused) {
    try {
      await mediaService.pauseAudio();
    } catch (e) {
      /* pause is best-effort; the monitor is still valid */
    }
  }
  const restoreOutbound = () => {
    // Only resume if WE paused it and the same producer is still live.
    if (producer && !wasPaused && mediaService.audioProducer === producer) {
      mediaService.resumeAudio().catch(() => {});
    }
  };

  // Fully RAW capture — every browser DSP stage off, chain-only processing.
  // EC would treat the monitor playback as echo (pumping / sustained-note
  // cancellation); NS adaptively fades stationary signals (steady tones/hums)
  // to zero within ~a second, which reads as a broken meter during a test.
  const RAW = {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  };
  const acquire = async (audioBase) => {
    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: audioDeviceId ? { ...audioBase, deviceId: { exact: audioDeviceId } } : audioBase,
      });
    } catch (exactErr) {
      return navigator.mediaDevices.getUserMedia({ audio: audioBase });
    }
  };

  let stream;
  try {
    stream = await acquire(RAW);
  } catch (err) {
    restoreOutbound();
    throw err;
  }

  if (liveChain) {
    // Swap the live chain onto the raw capture (replaceSource stops the old
    // EC'd raw track — the device now has exactly ONE, raw capture). The
    // producer keeps its stable processed track; it's paused anyway.
    liveChain.replaceSource(stream.getAudioTracks()[0]);
    if (settings) liveChain.updateSettings(settings);
    const track = liveChain.getProcessedTrack();
    let disposed = false;
    return {
      chain: liveChain,
      stream: track ? new MediaStream([track]) : null,
      dispose: () => {
        if (disposed) return;
        disposed = true;
        // Restore the user's real capture (EC/NS per their prefs), THEN
        // unmute. If the re-acquire fails the mic goes silent — same
        // semantics as a hardware unplug — rather than leaking a raw EC-less
        // capture into the room.
        (async () => {
          try {
            const back = await acquire({
              echoCancellation: restoreConstraints ? !!restoreConstraints.echoCancellation : true,
              noiseSuppression: restoreConstraints ? !!restoreConstraints.noiseSuppression : true,
              autoGainControl: false,
            });
            liveChain.replaceSource(back.getAudioTracks()[0]);
          } catch (e) {
            console.warn('⚠️ mic restore after self-listen failed:', e.message);
            liveChain.detachSource();
          }
          restoreOutbound();
        })();
      },
    };
  }

  const chain = new VoiceChain();
  chain.setLimiterEnabled(limiterEnabled);
  chain.attachSource(stream.getAudioTracks()[0]); // chain owns + stops the track
  if (settings) chain.updateSettings(settings);
  const track = chain.getProcessedTrack();
  return {
    chain,
    stream: track ? new MediaStream([track]) : null,
    dispose: () => {
      chain.dispose();
      restoreOutbound();
    },
  };
}

// Dev-only debug handle (mirrors window.__mediaService).
if (process.env.NODE_ENV !== 'production') {
  window.__voiceChain = voiceChain;
}
